
import React from 'react';
import moment from 'moment';
import DatePicker, { registerLocale } from 'react-datepicker';
import { enUS, fr } from 'date-fns/locale';
import styles from './FormFields.module.scss';
import dayjs from 'dayjs';
import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

// Register locales for react-datepicker
registerLocale('en', enUS);
registerLocale('fr', fr);

const MAX_TIME = new Date();
MAX_TIME.setHours(23);
MAX_TIME.setMinutes(59);

const MIN_TIME = new Date();
MIN_TIME.setHours(0);
MIN_TIME.setMinutes(0);

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'h:mm a';
const DATE_TIME_FORMAT = 'YYYY-MM-DD h:mm a';

interface TimeOnlyPickerFieldProps {
  name: string;
  compareDate?: Date | string | null;
  disabledAfter?: Date;
  disabledBefore?: Date;
  placeholderText?: string;
  onChange?: () => void;
  wrapperClassName?: string;
  className?: string;
  disabled?: boolean;
}

const TimeOnlyPickerField: React.FC<TimeOnlyPickerFieldProps> = ({
  name,
  compareDate,
  disabledAfter,
  disabledBefore,
  placeholderText,
  onChange = () => {},
  ...props
}) => {
  const { t, i18n } = useTranslation('events');
  const { setFieldValue, values, validate, errors } = useFormikContext();
  const placeholder = placeholderText || t('settings.discountCodes.page.defaultTimePlaceholder');
  
  // Get current locale for react-datepicker
  const currentLocale = i18n.language === 'fr' ? 'fr' : 'en';

  let currentValue = values[name];
  const currentDateValue = values[name.replace('Time', '')];

  if (!currentValue && currentDateValue) {
    currentValue = dayjs(currentDateValue).format(TIME_FORMAT);
  }

  const handleChange = (value: Date | null) => {
    if (!value) {
      return;
    }

    const day = moment(currentDateValue).format(DATE_FORMAT);
    const time = moment(value).format(TIME_FORMAT);
    const format = time ? DATE_TIME_FORMAT : DATE_FORMAT;
    const combined = moment(`${day} ${time || ''}`, format);

    if (combined.isValid()) {
      setFieldValue(name.replace('Time', ''), combined.toDate());
      setFieldValue(name, combined.format(TIME_FORMAT));
      onChange();
    }
  };

  return (
    <Field name={name.replace('Time', '')} validate={validate}>
      {({ meta }) => (
        <>
          <DatePicker
            onChange={handleChange}
            selected={
              currentValue
                ? moment(currentValue, ['h:m a', 'H:m']).toDate()
                : undefined
            }
            autoComplete="nope"
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            minTime={
              moment(disabledBefore).isSame(moment(compareDate), 'day')
                ? disabledBefore
                : MIN_TIME
            }
            maxTime={
              moment(disabledAfter).isSame(moment(compareDate), 'day')
                ? disabledAfter
                : MAX_TIME
            }
            dateFormat={currentLocale === 'fr' ? 'H:mm' : 'h:mm aa'}
            className={styles.timepickerField}
            placeholderText={placeholder}
            popperClassName={styles.popper}
            showPopperArrow={false}
            locale={currentLocale}
            {...props}
          />
          {meta.error && meta.touched && (
            <div className={styles.datepickerErrorMessage}>{meta.error}</div>
          )}
          {errors?.[name.replace('Time', '')] && (
            <div className={styles.datepickerErrorMessage}>
              {errors[name.replace('Time', '')]}
            </div>
          )}
        </>
      )}
    </Field>
  );
};

export default TimeOnlyPickerField;
